'use client';

import React, { useState, useEffect, useRef } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

// --- SHARED DATA ---
const TRAFFIC_DATA = [
    { label: "Organic Search", value: 54, color: "#10b981" },
    { label: "GMB Profile", value: 31, color: "#3b82f6" },
    { label: "Direct/Other", value: 11, color: "#ec4899" },
    { label: "Social", value: 4, color: "#f59e0b" }
];

const BAR_DATA = [
    { label: "Aug", value: 620 },
    { label: "Sep", value: 850 },
    { label: "Oct", value: 1050 },
    { label: "Nov", value: 1280 },
    { label: "Dec", value: 1550 },
];

const LINE_DATA = [180, 240, 210, 290, 340, 360];

// --- HELPER COMPONENTS ---

// 1. Donut Chart Component
const DonutChart = ({ isActive }: { isActive?: boolean }) => {
    const [segments, setSegments] = useState<any[]>([]);

    useEffect(() => {
        let cumulative = 0;
        const total = 100;
        const newSegments = TRAFFIC_DATA.map(item => {
            const startAngle = (cumulative / total) * 360;
            const endAngle = ((cumulative + item.value) / total) * 360;
            cumulative += item.value;
            const radius = 80;
            const cx = 100, cy = 100;
            const startRad = (startAngle - 90) * Math.PI / 180;
            const endRad = (endAngle - 90) * Math.PI / 180;
            const x1 = cx + radius * Math.cos(startRad);
            const y1 = cy + radius * Math.sin(startRad);
            const x2 = cx + radius * Math.cos(endRad);
            const y2 = cy + radius * Math.sin(endRad);
            const largeArc = item.value > 50 ? 1 : 0;
            return {
                ...item,
                d: `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`
            };
        });
        setSegments(newSegments);
    }, []);

    return (
        <div className="flex flex-col items-center h-full justify-center">
            <div className="relative w-48 h-48 mb-4">
                <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
                    {segments.map((seg, i) => (
                        <motion.path
                            key={i}
                            d={seg.d}
                            fill={seg.color}
                            stroke="#fff"
                            strokeWidth="2"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: isActive ? 1 : 0.8, opacity: isActive ? 1 : 0 }}
                            transition={{ delay: isActive ? i * 0.1 : 0, duration: 0.5 }}
                        />
                    ))}
                    <circle cx="100" cy="100" r="60" fill="white" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-slate-900">2.8k</span>
                    <span className="text-[10px] text-slate-500 uppercase">Visits</span>
                </div>
            </div>
            {/* Legend */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 w-full px-4">
                {TRAFFIC_DATA.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-xs text-slate-600 flex-1 truncate">{item.label}</span>
                        <span className="text-xs font-bold">{item.value}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// 2. Bar Chart Component
const BarChart = ({ isActive }: { isActive?: boolean }) => {
    const maxVal = Math.max(...BAR_DATA.map(d => d.value));
    return (
        <div className="flex flex-col h-full">
            <div className="flex items-end justify-between gap-2 h-40 mb-4 px-2">
                {BAR_DATA.map((bar, i) => (
                    <div key={i} className="flex flex-col items-center justify-end h-full flex-1">
                        <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: isActive ? `${(bar.value / maxVal) * 100}%` : 0 }}
                            transition={{ duration: 0.8, delay: isActive ? i * 0.1 : 0 }}
                            className="w-full max-w-[24px] bg-emerald-500 rounded-t-sm"
                        />
                        <span className="text-[10px] text-slate-400 mt-2">{bar.label}</span>
                    </div>
                ))}
            </div>
            <div className="bg-emerald-50 rounded-xl p-3 flex items-center justify-between">
                <div>
                    <div className="text-xs text-slate-500 font-medium">Monthly Growth</div>
                    <div className="text-lg font-bold text-emerald-600">+21%</div>
                </div>
                <div className="text-[10px] bg-white px-2 py-1 rounded-md text-slate-400 shadow-sm">
                    vs last mo
                </div>
            </div>
        </div>
    );
};

// 3. Rank Card (Animated)
const ScrollingRank = ({ value, isActive }: { value: number; isActive: boolean }) => {
    const spring = useSpring(50, { stiffness: 40, damping: 12 });
    const display = useTransform(spring, (current) => Math.round(current));

    useEffect(() => {
        if (isActive) {
            spring.set(50);
            const timeout = setTimeout(() => spring.set(value), 150);
            return () => clearTimeout(timeout);
        } else {
            spring.set(50);
        }
    }, [isActive, value, spring]);

    return <motion.span>{display}</motion.span>;
};

const RankCard = ({ isActive }: { isActive?: boolean }) => (
    <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-6 relative">
            <Icon name="StarIcon" className="w-10 h-10 text-orange-500" />
            <motion.div
                className="absolute inset-0 border-4 border-orange-200 rounded-full"
                animate={{ scale: [1, 1.1, 1], opacity: [1, 0, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
            />
        </div>
        <div className="text-5xl font-bold text-slate-900 mb-2 flex items-center justify-center gap-2">
            Top <ScrollingRank value={3} isActive={!!isActive} />
        </div>
        <div className="text-sm text-slate-500 font-medium px-8">
            Your business appears in the top 3 results for <span className="text-orange-500 font-bold">Local SEO</span> queries.
        </div>
    </div>
);

// 4. Line Chart (GMB)
const LineChart = ({ isActive }: { isActive?: boolean }) => {
    const [dPath, setDPath] = useState("");

    useEffect(() => {
        const width = 280;
        const height = 120;
        // Calculate points
        const points = LINE_DATA.map((val, i) => {
            const x = (i / (LINE_DATA.length - 1)) * width;
            const y = height - ((val / 400) * height);
            return `${x},${y}`;
        });

        // Create Path 'M x,y L x,y ...'
        if (points.length > 0) {
            const path = `M ${points[0]} ${points.slice(1).map(p => `L ${p}`).join(' ')}`;
            setDPath(path);
        }
    }, []);

    return (
        <div className="flex flex-col h-full justify-center">
            <div className="flex items-center justify-between mb-6 px-2">
                <div>
                    <div className="text-2xl font-bold text-slate-900">1.2k</div>
                    <div className="text-xs text-slate-500">Profile Views</div>
                </div>
                <div className="text-xs font-bold text-blue-500 bg-blue-50 px-2 py-1 rounded-full">
                    +18% Wk
                </div>
            </div>
            <div className="relative h-32 w-full">
                {dPath && (
                    <svg viewBox="0 0 280 120" className="w-full h-full overflow-visible">
                        {/* Area Fill */}
                        <path
                            d={`${dPath} L 280,120 L 0,120 Z`}
                            fill="url(#mobileLineGradient)"
                            opacity={isActive ? 0.2 : 0}
                            style={{ transition: 'opacity 1s ease-in-out' }}
                        />

                        {/* Animated Line */}
                        <motion.path
                            d={dPath}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: isActive ? 1 : 0 }}
                            transition={{ duration: 1.5, ease: "easeInOut" }}
                        />

                        <defs>
                            <linearGradient id="mobileLineGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
                                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                            </linearGradient>
                        </defs>
                    </svg>
                )}
            </div>
        </div>
    );
};

const CARDS = [
    { id: 'donut', title: 'Traffic Sources', Component: DonutChart },
    { id: 'bar', title: 'Organic Growth', Component: BarChart },
    { id: 'rank', title: 'Local Rank', Component: RankCard },
    { id: 'line', title: 'GMB Views', Component: LineChart },
];

const MobileBrandVisibilityMetrics = () => {
    // We use a scroll container with snap
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const onScroll = () => {
        if (containerRef.current) {
            const scrollLeft = containerRef.current.scrollLeft;
            // Get first child width to estimate Item Width
            const firstCard = containerRef.current.firstElementChild as HTMLElement;
            // Fallback to offsetWidth if no children (unlikely)
            const itemWidth = firstCard ? firstCard.offsetWidth : containerRef.current.offsetWidth;

            // Add a small buffer for gap (approx 16px) implicitly or explicitly
            // But since snap points are center/start, dividing by approximate card width is usually safest
            const index = Math.round(scrollLeft / itemWidth);
            setActiveIndex(index);
        }
    };

    return (
        <div className="w-full py-12 bg-slate-50 flex flex-col items-center">
            {/* Header */}
            <div className="text-center mb-8 px-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                    </span>
                    <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Live Pulse</span>
                </div>
                <h2 className="text-3xl font-bold text-slate-900 leading-tight">
                    Growth <span className="text-blue-600">Impact</span>
                </h2>
                <p className="text-sm text-slate-500 mt-2 max-w-[280px] mx-auto leading-relaxed">
                    Track your brand's acceleration across all digital touchpoints with our live performance dashboard.
                </p>
            </div>

            {/* CAROUSEL WRAPPER */}
            <div className="w-full relative">
                <div
                    ref={containerRef}
                    onScroll={onScroll}
                    className="flex overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] px-6 gap-4 pb-8"
                    style={{ scrollBehavior: 'smooth' }}
                >
                    {CARDS.map((card, i) => (
                        <div
                            key={card.id}
                            className="snap-center snap-always [scroll-snap-stop:always] shrink-0 w-full max-w-[320px] first:pl-2 last:pr-2"
                        >
                            <motion.div
                                className={`bg-white rounded-[32px] p-6 shadow-sm border border-slate-100 h-[380px] flex flex-col relative overflow-hidden transition-all duration-300 ${activeIndex === i ? 'scale-100 shadow-xl ring-1 ring-blue-500/10' : 'scale-95 opacity-70'}`}
                            >
                                <div className="mb-4 flex justify-between items-center">
                                    <h3 className="text-lg font-bold text-slate-900">{card.title}</h3>
                                    {activeIndex === i && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="w-1.5 h-1.5 rounded-full bg-green-500"
                                        />
                                    )}
                                </div>
                                <div className="flex-1 w-full">
                                    <card.Component isActive={activeIndex === i} />
                                </div>
                            </motion.div>
                        </div>
                    ))}
                </div>
            </div>

            {/* INDICATORS */}
            <div className="flex items-center gap-2 mt-2">
                {CARDS.map((_, i) => (
                    <div
                        key={i}
                        className={`h-1.5 rounded-full transition-all duration-300 ${activeIndex === i ? 'w-6 bg-blue-600' : 'w-1.5 bg-slate-200'}`}
                    />
                ))}
            </div>

        </div>
    );
};

export default MobileBrandVisibilityMetrics;
